import { createRouter, createWebHistory } from 'vue-router';
import { jwtDecode } from 'jwt-decode';

// Fallback JWT decoder in case jwt-decode fails
function jwtDecodeFallback(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(payload);
  } catch (e) {
    console.error('jwtDecode fallback failed:', e);
    return null;
  }
}

// Import your page components
import Login from '@/pages/Login.vue';
import Dashboard from '@/pages/Dashboard.vue';
import Models from '@/pages/Models.vue';
import Sales from '@/pages/Sales.vue';
import Reports from '@/pages/Reports.vue';
import Adjustments from '@/pages/Adjustments.vue';
import Calendar from '@/pages/Calendar.vue';
import Payments from '@/pages/Payments.vue';
import Configuration from '@/pages/Configuration.vue';

// Define your routes
const routes = [
  { path: '/', redirect: '/login' },
  { path: '/login', name: 'Login', component: Login },
  { 
    path: '/dashboard', 
    name: 'Dashboard', 
    component: Dashboard, 
    meta: { requiresAuth: true },
  },
  {
    path: '/models',
    name: 'Models',
    component: Models,
    meta: { requiresAuth: true },
  },
  {
    path: '/sales',
    name: 'Sales',
    component: Sales,
    meta: { requiresAuth: true },
  }, 
  {
    path: '/reports',
    name: 'Reports',
    component: Reports,
    meta: { requiresAuth: true },
  }, 
  {
    path: '/adjustments',
    name: 'Adjustments',
    component: Adjustments,
    meta: { requiresAuth: true },
  }, 
  {
    path: '/calendar',
    name: 'Calendar',
    component: Calendar,
    meta: { requiresAuth: true },
  },
  {
    path: '/payments',
    name: 'Payments',
    component: Payments,
    meta: { requiresAuth: true },
  },
  {
    path: '/configuration',
    name: 'Configuration',
    component: Configuration,
    meta: { requiresAuth: true },
  },
];

// Create the router instance
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Navigation Guard for authentication
router.beforeEach((to, from, next) => {
  console.log('--- Nav Guard Start ---');
  console.log(`Navigating from: ${from.path} -> to: ${to.path}`);

  // Retrieve authToken from localStorage
  const token = localStorage.getItem('authToken');
  if (token) {
    console.log(`Guard: Retrieved 'authToken' type: ${typeof token}, length: ${token.length}`);
    console.log(`Guard: First char: '${token[0]}', Last char: '${token[token.length - 1]}'`);
    console.log(`Guard: Retrieved 'authToken' raw value:`, token);
  } else {
    console.log("Guard: Retrieved 'authToken' is null or empty.");
  }

  let isAuthenticated = false;
  let decoded = null;

  if (token) {
    // Remove potential extra quotes
    let tokenToDecode = token;
    if (typeof token === 'string' && token.length > 1 && token.startsWith('"') && token.endsWith('"')) {
      console.warn("Guard: Token found wrapped in extra quotes. Removing them.");
      tokenToDecode = token.substring(1, token.length - 1);
      console.log("Guard: Token after removing quotes:", tokenToDecode ? `"${tokenToDecode.substring(0, 15)}..."` : tokenToDecode);
    } else {
      console.log("Guard: Token does not appear to have extra quotes.");
      tokenToDecode = token;
    }

    try {
      console.log("Guard: Attempting jwtDecode with token:", tokenToDecode ? `"${tokenToDecode.substring(0, 15)}..."` : tokenToDecode);
      try {
        decoded = jwtDecode(tokenToDecode);
      } catch (decodeError) {
        console.error('jwt-decode failed, using fallback:', decodeError);
        decoded = jwtDecodeFallback(tokenToDecode);
      }

      if (!decoded) {
        throw new Error('Failed to decode token using both jwt-decode and fallback');
      }

      const currentTime = Math.floor(Date.now() / 1000);
      console.log('Guard: Decoded token:', decoded);
      console.log(`Guard: Current Time (s): ${currentTime} (${new Date(currentTime * 1000).toISOString()})`);
      console.log(`Guard: Token Expires (s): ${decoded.exp} (${new Date(decoded.exp * 1000).toISOString()})`);

      if (decoded.exp && decoded.exp > currentTime) {
        console.log('Guard: Token is VALID.');
        isAuthenticated = true;
      } else {
        console.warn('Guard: Token has EXPIRED.');
        localStorage.removeItem('authToken'); // Clear the expired token
        isAuthenticated = false;
      }
    } catch (error) {
      console.error('🔥🔥 GUARD JWTDECODE FAILED 🔥🔥');
      console.error('Error message:', error.message);
      console.error('Failed token was:', tokenToDecode);
      localStorage.removeItem('authToken'); // Clear the invalid token
      isAuthenticated = false;
    }
  } else {
    console.log('Guard: No token found in localStorage.');
  }

  const requiresAuth = !!to.meta?.requiresAuth;
  console.log(`Guard: Route requires auth: ${requiresAuth}, User is authenticated: ${isAuthenticated}`);

  if (requiresAuth && !isAuthenticated) {
    console.log('🔐 Guard: Access DENIED. Redirecting to login.');
    next('/login');
  } else if (to.path === '/login' && isAuthenticated) {
    console.log('🔄 Guard: Already authenticated. Redirecting to dashboard.');
    next('/dashboard');
  } else {
    console.log('✅ Guard: Access GRANTED. Proceeding.');
    next();
  }

  console.log('--- Nav Guard End ---');
});

export default router;